import { BigNumber, mustNeverExist } from "@repo/utils";
import type { PurchaseOrderExtraCalculation } from "./types.js";

const zero = BigNumber(0);

export function calculateGrossTotal(
    quantity: BigNumber | string | number | null,
    unitPrice: BigNumber | string | number | null,
    priceUnit: BigNumber | string | number | null,
    uomConversionCoefficient: BigNumber | number = 1,
    decimalPlaces?: number,
) {
    if (quantity === null || unitPrice === null) {
        return zero;
    }
    const price = BigNumber(unitPrice);
    return price
        .multipliedBy(quantity)
        .dividedBy(priceUnit ?? 1)
        .multipliedBy(uomConversionCoefficient)
        .decimalPlaces(decimalPlaces ?? price.decimalPlaces() ?? 2);
}

export function calculateNetTotal(
    grossTotal: BigNumber | number,
    extra: Array<{ calculation: PurchaseOrderExtraCalculation }> | null | undefined,
    decimalPlaces: number,
    scale: BigNumber | number = 1,
) {
    let netTotal = BigNumber.isBigNumber(grossTotal) ? grossTotal : BigNumber(grossTotal);

    let netCalculations: PurchaseOrderExtraCalculation[] = [];

    for (let { calculation } of extra ?? []) {
        if (calculation.class === "percentage" && calculation.appliedTo === "net") {
            netCalculations.push(calculation);
        }

        netTotal = netTotal.plus(calculate(BigNumber(grossTotal), calculation, scale));
    }

    for (let calculation of netCalculations) {
        netTotal = netTotal.plus(calculate(netTotal, calculation, scale));
    }

    return netTotal.decimalPlaces(decimalPlaces);
}

function calculate(value: BigNumber, calculation: PurchaseOrderExtraCalculation, scale: BigNumber | number) {
    switch (calculation.class) {
        case "percentage":
            return calculation.rate === null ? zero : value.multipliedBy(calculation.rate).dividedBy(100);
        case "fixedAmount":
            return BigNumber(calculation.amount ?? 0).multipliedBy(scale);
        case "quantityBased":
            return calculateGrossTotal(
                calculation.quantity,
                calculation.unitPrice,
                calculation.perAmount,
                // you do not need to apply uomConversionCoefficient because the condition quantity is already converted and the coefficient is for backward conversion
                // calculation.uomConversionCoefficient,
            ).multipliedBy(scale);
        default:
            throw mustNeverExist(calculation);
    }
}

export function calculateNetUnitPrice(
    grossUnitPrice: BigNumber | number,
    quantity: BigNumber | number,
    priceUnit: BigNumber | number,
    uomConversionCoefficient: BigNumber | number,
    extra: Array<{ calculation: PurchaseOrderExtraCalculation }> | null | undefined,
    decimalPlaces: number,
) {
    const scale = BigNumber(priceUnit).dividedBy(quantity).dividedBy(uomConversionCoefficient);
    return calculateNetTotal(grossUnitPrice, extra, decimalPlaces, scale);
}
